import { useState, useMemo, useCallback } from 'react';
import { IconCode, IconCopy, IconPlus, IconTrash } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import useTheme from 'chat-ui/src/hooks/useTheme';
import getTextColor from 'chat-ui/src/hooks/getTextColorContrast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import useTranslation from '@/hooks/useTranslation';
import languagesList from '@/lang/languages/languagesList';

interface EmbedCodeGeneratorProps {
  projectLink: string;
  projectInputMethods?: string[];
}

interface CustomParam {
  id: string;
  key: string;
  value: string;
}

interface LanguageOption {
  id: number;
  label: string;
  value: string;
}

const INPUT_METHOD_OPTIONS = [
  { id: 'text', label: 'Text' },
  { id: 'audio', label: 'Voice' },
  { id: 'video', label: 'Video' },
  { id: 'image', label: 'Image' },
];

const DIMENSION_PRESETS = [
  { name: 'textOnly', width: '100%', height: '600px' },
  { name: 'voice', width: '100%', height: '650px' },
  { name: 'video', width: '100%', height: '800px' },
  { name: 'mobile', width: '100%', height: '100vh' },
];

function EmbedCodeGenerator({ projectLink, projectInputMethods = [] }: EmbedCodeGeneratorProps) {
  const { lang } = useTranslation();
  const { currentTheme } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Form state
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption | null>(null);
  const [skipConsent, setSkipConsent] = useState(false);
  const [inputMethods, setInputMethods] = useState<string[]>([]);
  const [environment, setEnvironment] = useState<'production' | 'sandbox'>('production');
  const [customParams, setCustomParams] = useState<CustomParam[]>([
    { id: crypto.randomUUID(), key: 'participant_id', value: '{{ID}}' },
  ]);
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('600px');

  // Validation
  const validationError = useMemo(() => {
    if (skipConsent && !selectedLanguage) {
      return lang.get('msg.embedGeneratorLanguageRequired');
    }
    return null;
  }, [skipConsent, selectedLanguage, lang]);

  // Build URL with parameters
  const generatedUrl = useMemo(() => {
    const params = new URLSearchParams();

    if (selectedLanguage) {
      // Use full English name (label) as backend expects this format
      params.set('language', selectedLanguage.label);
    }
    if (skipConsent) {
      params.set('skipConsent', 'true');
    }
    if (inputMethods.length > 0) {
      params.set('inputMethods', inputMethods.join(','));
    }
    if (environment === 'sandbox') {
      params.set('environment', 'sandbox');
    }

    customParams.forEach((param) => {
      if (param.key.trim() && param.value.trim()) {
        params.set(param.key.trim(), param.value.trim());
      }
    });

    const queryString = params.toString();
    return queryString ? `${projectLink}?${queryString}` : projectLink;
  }, [projectLink, selectedLanguage, skipConsent, inputMethods, environment, customParams]);

  // Generate iframe code
  const iframeCode = useMemo(() => {
    return `<iframe src="${generatedUrl}" allow="camera; microphone; autoplay;" title="Tellet" width="${width}" height="${height}" frameBorder="0"></iframe>`;
  }, [generatedUrl, width, height]);

  // Handlers
  const handleInputMethodChange = useCallback((methodId: string, checked: boolean) => {
    setInputMethods((prev) => (checked ? [...prev, methodId] : prev.filter((m) => m !== methodId)));
  }, []);

  const handleAddCustomParam = useCallback(() => {
    setCustomParams((prev) => [...prev, { id: crypto.randomUUID(), key: '', value: '' }]);
  }, []);

  const handleRemoveCustomParam = useCallback((index: number) => {
    setCustomParams((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCustomParamChange = useCallback(
    (index: number, field: 'key' | 'value', value: string) => {
      setCustomParams((prev) =>
        prev.map((param, i) => (i === index ? { ...param, [field]: value } : param)),
      );
    },
    [],
  );

  const handlePresetClick = useCallback((preset: (typeof DIMENSION_PRESETS)[0]) => {
    setWidth(preset.width);
    setHeight(preset.height);
  }, []);

  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(iframeCode);
      toast.success(lang.get('msg.codeCopied'));
    } catch {
      toast.error(lang.get('msg.codeNotCopied'));
    }
  }, [iframeCode, lang]);

  const projectMethodsHint =
    projectInputMethods.length > 0
      ? projectInputMethods
          .map((m) => {
            if (m === 'audio') return 'Voice';
            return m.charAt(0).toUpperCase() + m.slice(1);
          })
          .join(', ')
      : 'Text, Voice, Video';

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="default" className="flex items-center gap-1 px-6" type="button">
          <IconCode size={20} />
          {lang.get('msg.embed')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto z-[101]">
        <DialogHeader>
          <DialogTitle className="mb-4">{lang.get('msg.embedGeneratorTitle')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          {/* Language Selection */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{lang.get('msg.embedGeneratorLanguage')}</label>
            <Select
              value={selectedLanguage}
              onChange={(option) => setSelectedLanguage(option as LanguageOption | null)}
              options={languagesList.language}
              isClearable
              placeholder={lang.get('msg.embedGeneratorSelectLanguage')}
              className="text-sm"
              styles={{
                control: (styles) => ({
                  ...styles,
                  backgroundColor: '#FFFFFF',
                  borderColor: currentTheme?.['input-invalid-color'],
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? currentTheme?.primary : '#FFFFFF',
                  color: state.isFocused
                    ? getTextColor(currentTheme?.primary as string)
                    : '#000000',
                  ':active': { backgroundColor: currentTheme?.primary },
                }),
              }}
            />
            <p className="text-xs text-gray-500">{lang.get('msg.embedGeneratorLanguageHint')}</p>
          </div>

          {/* Consent Options */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{lang.get('msg.embedGeneratorConsent')}</label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="consent"
                  checked={!skipConsent}
                  onChange={() => setSkipConsent(false)}
                  className="w-4 h-4"
                />
                <span className="text-sm">{lang.get('msg.embedGeneratorShowConsent')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="consent"
                  checked={skipConsent}
                  onChange={() => setSkipConsent(true)}
                  className="w-4 h-4"
                />
                <span className="text-sm">{lang.get('msg.embedGeneratorSkipConsent')}</span>
              </label>
            </div>
            {skipConsent && (
              <p className="text-xs text-orange-600">
                {lang.get('msg.embedGeneratorSkipConsentHint')}
              </p>
            )}
            {validationError && <p className="text-xs text-red-600">{validationError}</p>}
          </div>

          {/* Input Methods Override */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              {lang.get('msg.embedGeneratorInputMethods')}
            </label>
            <div className="flex flex-wrap gap-4">
              {INPUT_METHOD_OPTIONS.map((method) => (
                <label key={method.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={inputMethods.includes(method.id)}
                    onCheckedChange={(checked) =>
                      handleInputMethodChange(method.id, checked as boolean)
                    }
                  />
                  <span className="text-sm">{method.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              {lang
                .get('msg.embedGeneratorInputMethodsHint')
                .replace('{methods}', projectMethodsHint)}
            </p>
          </div>

          {/* Environment */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              {lang.get('msg.embedGeneratorEnvironment')}
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="environment"
                  checked={environment === 'production'}
                  onChange={() => setEnvironment('production')}
                  className="w-4 h-4"
                />
                <span className="text-sm">{lang.get('msg.embedGeneratorProduction')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="environment"
                  checked={environment === 'sandbox'}
                  onChange={() => setEnvironment('sandbox')}
                  className="w-4 h-4"
                />
                <span className="text-sm">{lang.get('msg.embedGeneratorSandbox')}</span>
              </label>
            </div>
          </div>

          {/* Custom Parameters */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              {lang.get('msg.embedGeneratorCustomParams')}
            </label>
            <div className="flex flex-col gap-2">
              {customParams.map((param, index) => (
                <div key={param.id} className="flex items-center gap-2">
                  <Input
                    placeholder="Key"
                    value={param.key}
                    onChange={(e) => handleCustomParamChange(index, 'key', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Value"
                    value={param.value}
                    onChange={(e) => handleCustomParamChange(index, 'value', e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveCustomParam(index)}
                    className="shrink-0"
                    type="button"
                  >
                    <IconTrash size={16} />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddCustomParam}
                className="w-fit"
                type="button"
              >
                <IconPlus size={16} className="mr-1" />
                {lang.get('msg.embedGeneratorAddParam')}
              </Button>
            </div>
          </div>

          {/* iframe Dimensions */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              {lang.get('msg.embedGeneratorDimensions')}
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {DIMENSION_PRESETS.map((preset) => (
                <Button
                  key={preset.name}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePresetClick(preset)}
                  type="button"
                >
                  {lang.get(
                    `msg.embedGeneratorPreset${preset.name.charAt(0).toUpperCase() + preset.name.slice(1)}`,
                  )}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {lang.get('msg.embedGeneratorWidth')}:
                </span>
                <Input value={width} onChange={(e) => setWidth(e.target.value)} className="w-24" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {lang.get('msg.embedGeneratorHeight')}:
                </span>
                <Input
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="w-24"
                />
              </div>
            </div>
          </div>

          {/* Generated Code */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{lang.get('msg.embedGeneratorCode')}</label>
            <div className="relative">
              <pre className="p-4 bg-gray-100 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">
                {iframeCode}
              </pre>
              <Button
                variant="default"
                size="sm"
                onClick={handleCopyCode}
                className="absolute top-2 right-2"
                type="button"
                disabled={!!validationError}
              >
                <IconCopy size={16} className="mr-1" />
                {lang.get('msg.copyCode')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EmbedCodeGenerator;
